import { supabase } from '../config/supabase';
import {
  defaultEndTimeFromPickup,
  scheduleWindowsOverlap,
  type ScheduleWindow,
} from '../utils/scheduleConflict';

export interface ScheduleConflictRow {
  conflicting_trip_id: string;
  pickup_time: string;
  end_time: string;
  pickup_address: string;
  status: string;
}

export async function checkVehicleScheduleConflict(params: {
  vehicleId: string;
  date: string;
  pickupTime: string;
  endTime: string;
  excludeTripId?: string;
  bufferMinutes?: number;
}): Promise<ScheduleConflictRow[]> {
  const { data, error } = await supabase.rpc('check_vehicle_schedule_conflict', {
    p_vehicle_id: params.vehicleId,
    p_date: params.date,
    p_pickup_time: params.pickupTime,
    p_end_time: params.endTime,
    p_exclude_trip_id: params.excludeTripId ?? null,
    p_buffer_minutes: params.bufferMinutes ?? null,
  });

  if (error) throw new Error(error.message);
  return (data ?? []) as ScheduleConflictRow[];
}

export async function checkDriverScheduleConflict(params: {
  driverId: string;
  date: string;
  pickupTime: string;
  endTime: string;
  excludeTripId?: string;
  bufferMinutes?: number;
}): Promise<ScheduleConflictRow[]> {
  const { data, error } = await supabase.rpc('check_driver_schedule_conflict', {
    p_driver_id: params.driverId,
    p_date: params.date,
    p_pickup_time: params.pickupTime,
    p_end_time: params.endTime,
    p_exclude_trip_id: params.excludeTripId ?? null,
    p_buffer_minutes: params.bufferMinutes ?? null,
  });

  if (error) throw new Error(error.message);
  return (data ?? []) as ScheduleConflictRow[];
}

export function formatScheduleConflictMessage(rows: ScheduleConflictRow[]): string {
  return rows
    .map(
      (r) =>
        `Schedule conflict with trip at ${r.pickup_time}–${r.end_time} (${r.pickup_address}) — status: ${r.status}`
    )
    .join('\n');
}

export function resolveCandidateWindow(
  pickupTime?: string | null,
  endTime?: string | null
): ScheduleWindow | null {
  if (!pickupTime) return null;
  return {
    pickupTime,
    endTime: endTime ?? defaultEndTimeFromPickup(pickupTime),
  };
}

export function tripRowOverlapsCandidate(
  row: { pickup_time: string; end_time?: string | null },
  candidate: ScheduleWindow,
  bufferMinutes = 15
): boolean {
  const block: ScheduleWindow = {
    pickupTime: row.pickup_time,
    endTime: row.end_time ?? defaultEndTimeFromPickup(row.pickup_time),
  };
  return scheduleWindowsOverlap(block, candidate, bufferMinutes);
}
